import { StudentAssignmentItemWithAttempt } from '../../../constants/types/student/homework/type';
import TextWinds from '../../common/TextWinds';
import { Button } from 'antd';
import openPopupWindow from '../../../util/popupWindow';
import { formatDueDateText } from '../../../api/utils/util-func';
import { useParams } from 'react-router-dom';
import {
  CookieKey,
  CookieTypeJebsAiRecitation,
  CookieTypeUserInfo,
  createCookie,
} from '../../../util/cookie';
import {
  SUBJECT_COLOR_MAP,
  SUBJECT_LABEL_MAP,
} from '../../../constants/types/lms/type';
import { useAppDispatch } from '../../../store/hook';
import { openAlert, openConfirm } from '../../../store/features/modalSlice';

interface StudentRecitationAssignmentCardProps {
  assignment: StudentAssignmentItemWithAttempt;
  variant: 'urgent' | 'normal';
}

const getAssignmentActionMeta = (
  assignment: StudentAssignmentItemWithAttempt,
) => {
  if (assignment.status === 'COMPLETED') {
    const attemptCount = assignment.attemptCount ?? 0;
    const attemptLimit = assignment.attemptLimit ?? 0;

    const canRetry = attemptCount < attemptLimit;

    return {
      label: canRetry ? `재응시` : '완료',
      isVisible: canRetry,
      isDisabled: false,
    };
  }

  if (
    assignment.status === 'NOT_STARTED' ||
    assignment.status === 'IN_PROGRESS'
  ) {
    return {
      label: '응시하기',
      isVisible: true,
      isDisabled: false,
    };
  }

  if (assignment.status === 'PROCESSING_RESULT') {
    return {
      label: 'AI분석 중',
      isVisible: true,
      isDisabled: true,
    };
  }

  return {
    label: '오류',
    isVisible: false,
    isDisabled: true,
  };
};

/**
 * 개편 LMS (학생) - 클래스 상세 페이지 > 콘텐츠 판넬 > 우측 [할 일 목록] > 젭스온(AI인증제) 카드
 */
const StudentRecitationAssignmentCard = ({
  assignment,
  variant,
}: StudentRecitationAssignmentCardProps) => {
  const { classId } = useParams();

  const dispatch = useAppDispatch();

  const {
    setCookie: setJebsAiRecitationCookie,
    removeCookie: removeJebsAiRecitationCookie,
  } = createCookie<CookieTypeJebsAiRecitation>(CookieKey.JEBS_AI_RECITATION);

  const { getCookie: getUserInfoCookie } = createCookie<CookieTypeUserInfo>(
    CookieKey.USER_INFO,
  );

  const handleRecitationOpen = (isTest: boolean) => {
    try {
      // 기존 AI인증제 쿠키 삭제
      removeJebsAiRecitationCookie();
    } catch (error) {
      // 쿠키가 없는 경우 무시
      // console.error(error);
    }

    // 유저 인포 쿠키 가져옴
    const { studentId, uid } = getUserInfoCookie();

    // 새로운 뉴인증제 쿠키 생성
    setJebsAiRecitationCookie(
      {
        student: {
          id: studentId,
          uid: uid,
        },
        teacher: {
          id: '',
          uid: '',
          classId: classId,
        },
        todo: {
          id: assignment.todoId,
          type: 'JEBS_AI_RECITATION',
        },
        accessToken: localStorage.getItem('accessToken') || '',
      },
      1 / 24,
    );

    // 뉴인증제 창 열기
    openPopupWindow(
      `${assignment.link}?isTest=${isTest ? 'true' : 'false'}`,
      1440,
      1440 / 1080,
    );
  };

  const handleAssignmentEnterClick = (isTest: boolean = true) => {
    if (isTest) {
      if (assignment.attemptCount === 0) {
        /** 응시하기 */
        handleRecitationOpen(isTest);
      } else if (assignment.attemptCount === 1) {
        /** 재응시 */
        dispatch(
          openConfirm({
            message: (
              <div className="flex flex-col items-center text-center md:my-3">
                <div className="text-base font-bold text-gray9">
                  이번이 마지막 기회입니다.
                </div>

                <div className="mt-1 text-sm font-medium text-gray7">
                  다시 도전해 볼까요?
                </div>
              </div>
            ),
            confirmText: '계속하기',
            cancelText: '닫기',
            onConfirm: () => {
              handleRecitationOpen(isTest);
            },
          }),
        );
      } else {
        dispatch(openAlert({ message: '남은 응시기회가 없습니다.' }));
      }
    } else {
      /** 연습하기 */
      handleRecitationOpen(isTest);
    }
  };

  const isUrgent = variant === 'urgent';

  const assignmentActionMeta = getAssignmentActionMeta(assignment);

  return (
    <article
      className={[
        'overflow-hidden rounded-xl border border-gray3 bg-white shadow-sm',
        isUrgent
          ? 'border-l-4 border-l-red-500'
          : 'border-l-4 border-l-[#0B8ED0]',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-4">
        <div className="flex min-w-0 flex-1 items-center gap-3 xl:gap-4">
          <div
            className={[
              'flex h-12 w-12 shrink-0 items-center justify-center whitespace-pre-line rounded-xl text-center font-extrabold leading-tight',
              SUBJECT_COLOR_MAP[assignment.type],
            ].join(' ')}
          >
            {SUBJECT_LABEL_MAP[assignment.type]}
          </div>

          <div className="min-w-0 flex-1">
            <TextWinds
              type="content_body2"
              color="gray9"
              className="break-keep text-sm font-bold leading-snug xl:line-clamp-1 xl:text-base"
            >
              {`${assignment.book} · ${assignment.title}`}
            </TextWinds>

            <TextWinds
              type="content_body3"
              color="gray7"
              className="mt-1 block text-xs xl:text-sm"
            >
              {formatDueDateText(assignment.dueDate)}
            </TextWinds>
          </div>
        </div>

        {assignmentActionMeta.isVisible && (
          <div className="flex shrink-0 flex-col items-stretch gap-2 md:flex-row md:items-center">
            <Button
              type="primary"
              className="flex w-full shrink-0 justify-center rounded-lg !border-purple5 px-4 !text-purple5 hover:!border-purple6 hover:!bg-purple1 md:w-auto"
              onClick={() => handleAssignmentEnterClick(false)}
            >
              연습하기
            </Button>

            <Button
              type="primary"
              className="flex w-full shrink-0 justify-center rounded-lg !border-purple5 !bg-purple5 px-4 hover:!border-purple6 hover:!bg-purple6 md:w-auto"
              disabled={assignmentActionMeta.isDisabled}
              onClick={() => handleAssignmentEnterClick(true)}
            >
              {assignmentActionMeta.label}
            </Button>
          </div>
        )}
      </div>
    </article>
  );
};

export default StudentRecitationAssignmentCard;
